/**
 * TRENDING PRODUCTS SLIDER (slider-trending)
 *
 * Desktop is a CSS grid (cards per row comes from the section setting), mobile is a Swiper
 * carousel with dots. The cards can arrive after render — with the Shopify recommendations
 * source the theme's <product-recommendations> element fetches them over AJAX and replaces its
 * children — so both the Swiper init and the Add to Bag wiring run again on every mutation.
 */
(() => {
  const MOBILE_MAX_WIDTH = 768;

  class SliderTrending extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.isSettingUp = false;
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.setup();

      /* The recommendations endpoint answers after this element is already connected.
         Swiper adds nodes of its own (the pagination bullets), so mutations raised by our
         own setup are ignored — otherwise the observer would re-enter forever. */
      this.observer = new MutationObserver(() => {
        if (this.isSettingUp) return;
        this.setup();
      });
      this.observer.observe(this, { childList: true, subtree: true });

      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      if (this.observer) this.observer.disconnect();
      this.destroySwiper();
    }

    setup() {
      this.isSettingUp = true;
      this.bindAddToCart();
      this.syncSwiper();
      /* Released after the observer has flushed the mutations we just caused. */
      requestAnimationFrame(() => {
        this.isSettingUp = false;
      });
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.syncSwiper(), 150);
    }

    /* ------------------------------------------------------------------ Swiper */

    syncSwiper() {
      const carousel = this.querySelector('.slider-trending__carousel');
      if (!carousel || typeof Swiper === 'undefined') return;

      const isMobile = window.innerWidth <= MOBILE_MAX_WIDTH;

      if (!isMobile) {
        this.destroySwiper();
        return;
      }

      /* Already running on the same DOM — nothing to do. */
      if (this.swiper && this.swiper.el === carousel && !this.swiper.destroyed) {
        this.swiper.update();
        return;
      }

      this.destroySwiper();

      const perView = parseFloat(this.getAttribute('data-mobile-per-view')) || 2;
      const gap = parseInt(
        getComputedStyle(this).getPropertyValue('--st-gap-mobile') || '16',
        10
      );

      this.swiper = new Swiper(carousel, {
        slidesPerView: perView,
        slidesPerGroup: Math.max(1, Math.floor(perView)),
        spaceBetween: gap,
        watchOverflow: true,
        grabCursor: true,
        threshold: 5,
        speed: 300,
        pagination: {
          el: carousel.querySelector('.slider-trending__pagination'),
          clickable: true,
        },
      });
    }

    destroySwiper() {
      if (this.swiper && !this.swiper.destroyed) {
        this.swiper.destroy(true, true);
      }
      this.swiper = null;
    }

    /* ------------------------------------------------------------- Add to bag */

    bindAddToCart() {
      this.querySelectorAll('.slider-trending__add:not([data-atc-bound])').forEach((button) => {
        button.setAttribute('data-atc-bound', '');
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.addToCart(button);
        });
      });
    }

    addToCart(button) {
      const variantId = button.getAttribute('data-variant-id');
      if (!variantId || button.disabled || button.classList.contains('is-adding')) return;

      const originalLabel = button.getAttribute('data-atc-label') || button.textContent.trim();

      button.classList.add('is-adding');
      button.disabled = true;
      button.textContent = 'ADDING…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then((response) => {
          if (!response.ok) throw new Error('Add to cart failed');
          return response.json();
        })
        .then((data) => {
          button.textContent = 'ADDED ✓';
          button.classList.remove('is-adding');
          button.classList.add('is-added');

          /* Same two events the other carousels dispatch, so the header count and the
             cart drawer both refresh. */
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true })
          );

          setTimeout(() => {
            button.textContent = originalLabel;
            button.classList.remove('is-added');
            button.disabled = false;
          }, 2000);
        })
        .catch(() => {
          button.textContent = originalLabel;
          button.classList.remove('is-adding');
          button.disabled = false;
        });
    }
  }

  if (!window.customElements.get('slider-trending')) {
    window.customElements.define('slider-trending', SliderTrending);
  }
})();
